import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/currency';
import {
  TrendingUp, Users, Building2, Wallet, Loader2,
  DollarSign, BarChart3, ArrowUpRight, CheckCircle2, RefreshCw
} from 'lucide-react';

export default function AdminAnalyticsDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/metrics/platform');
      setMetrics(res.data);
    } catch (err) {
      console.error('Failed to load platform metrics:', err);
      setError('Failed to load platform metrics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMetrics(); }, []);

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#4ADE80] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10">
        <div className="bg-red-500/10 border border-red-500/30 p-6 text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={fetchMetrics} className="px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Capital Committed', value: formatINR(metrics.capitalCommitted), icon: TrendingUp, accent: 'text-[#4ADE80]', bgAccent: 'bg-[#4ADE80]/10' },
    { label: 'Capital Confirmed', value: formatINR(metrics.capitalConfirmed), icon: CheckCircle2, accent: 'text-emerald-400', bgAccent: 'bg-emerald-400/10' },
    { label: 'Capital Received', value: formatINR(metrics.capitalReceived), icon: DollarSign, accent: 'text-blue-400', bgAccent: 'bg-blue-400/10' },
    { label: 'Total Commission', value: formatINR(metrics.totalCommission), icon: Wallet, accent: 'text-purple-400', bgAccent: 'bg-purple-400/10' },
  ];

  const countCards = [
    { label: 'Total Investors', value: metrics.totalInvestors, sub: `${metrics.activeInvestors} active`, icon: Users },
    { label: 'Approved Brokers', value: metrics.totalBrokers, icon: ArrowUpRight },
    { label: 'Active Projects', value: metrics.activeProjects, sub: `${metrics.openUnits} open units`, icon: Building2 },
    { label: 'Deals w/ Investments', value: metrics.dealsWithInvestments, icon: BarChart3 },
  ];

  return (
    <div className="p-6 md:p-10" data-testid="admin-analytics-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-light text-white mb-1">Platform Analytics</h1>
          <p className="text-sm text-gray-500">Centralized metrics from the single source of truth</p>
        </div>
        <button
          onClick={fetchMetrics}
          className="flex items-center gap-2 px-4 py-2 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-colors text-sm"
          data-testid="refresh-platform-metrics"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Financial KPIs */}
      <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-3">Financial Overview</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 mb-8" data-testid="platform-kpi-cards">
        {kpiCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div key={idx} className="bg-black px-5 py-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-gray-600 uppercase tracking-widest">{card.label}</p>
                <div className={`w-7 h-7 flex items-center justify-center ${card.bgAccent}`}>
                  <Icon className={`w-3.5 h-3.5 ${card.accent}`} />
                </div>
              </div>
              <p className={`font-mono text-2xl font-light ${card.accent}`} data-testid={`kpi-${card.label.toLowerCase().replace(/ /g, '-')}`}>
                {card.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Counts */}
      <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-3">Platform Counts</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10 mb-8" data-testid="platform-count-cards">
        {countCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div key={idx} className="bg-black px-5 py-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] text-gray-600 uppercase tracking-widest">{card.label}</p>
                <Icon className="w-4 h-4 text-gray-700" />
              </div>
              <p className="font-mono text-3xl font-light text-white" data-testid={`count-${card.label.toLowerCase().replace(/ /g, '-')}`}>
                {card.value}
              </p>
              {card.sub && <p className="text-xs text-gray-600 mt-2">{card.sub}</p>}
            </div>
          );
        })}
      </div>

      {/* Commission Breakdown */}
      <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-3">Commission Summary</p>
      <div className="border border-white/10 bg-black">
        <div className="grid grid-cols-3 gap-px bg-white/5">
          <div className="bg-black px-5 py-5">
            <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2">Total Booked</p>
            <p className="font-mono text-xl text-purple-400">{formatINR(metrics.totalCommission)}</p>
          </div>
          <div className="bg-black px-5 py-5">
            <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2">Paid Out</p>
            <p className="font-mono text-xl text-emerald-400">{formatINR(metrics.paidCommission)}</p>
          </div>
          <div className="bg-black px-5 py-5">
            <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2">Pending</p>
            <p className="font-mono text-xl text-yellow-400">{formatINR(Math.max(0, metrics.totalCommission - metrics.paidCommission))}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
